import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import * as XLSX from "xlsx";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";

// Icons
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MapIcon from "@mui/icons-material/Map";

// ===== Leaflet / React-Leaflet =====
import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix icon paths (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom markers
const edificioIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const cierreIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const ListadoJornadas = () => {
  const { auth } = useAuth();
  const toast = useToast();

  const [jornadas, setJornadas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [centros, setCentros] = useState([]);

  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [filtroCentro, setFiltroCentro] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ===== Modal edición =====
  const [modalEditar, setModalEditar] = useState(false);
  const [jornadaEditar, setJornadaEditar] = useState(null);

  const [editInicio, setEditInicio] = useState("");
  const [editFin, setEditFin] = useState("");
  const [editMotivo, setEditMotivo] = useState("");
  const [editObs, setEditObs] = useState("");
  const [editError, setEditError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // ===== Modal mapa =====
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio");
  const [ubicacionInicio, setUbicacionInicio] = useState(null);
  const [ubicacionCierre, setUbicacionCierre] = useState(null);
  const [ubicacionCentro, setUbicacionCentro] = useState(null);

  // --------------------------
  // Scope / capacidades
  // --------------------------
  const esAdmin = auth?.rol === "admin";
  const esGerente = auth?.rol === "gerente";
  const esUsuario = !esAdmin && !esGerente;

  const puedeValidar = esAdmin;
  const puedeEditar = esAdmin || esGerente;

  // --------------------------
  // Helpers
  // --------------------------
  const parseDateSafe = (value) => {
    if (!value) return null;

    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toDatetimeLocalString = (dateValue) => {
    if (!dateValue) return "";
    const d = parseDateSafe(dateValue);
    if (!d) return "";

    const pad = (n) => String(n).padStart(2, "0");

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  };

  const calcularDuracion = (ms) => {
    if (!ms || Number.isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  const nombreUsuario = (id) => {
    const u = usuarios.find((x) => String(x.id) === String(id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : "-";
  };

  const nombreValidador = (id) => {
    if (!id) return "-";
    const u = usuarios.find((x) => String(x.id) === String(id));
    return u ? `${u.nombre} ${u.apellidos || ""}`.trim() : `ID ${id}`;
  };

  const fmtBoolSN = (b) => (b ? "Sí" : "No");

  // --------------------------
  // Carga inicial
  // --------------------------
  useEffect(() => {
    if (!auth?.id) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    try {
      const [resUsuarios, resCentros, resJornadas] = await Promise.all([
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}jornadas/finalizadas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      const usuariosData = resUsuarios.ok ? await resUsuarios.json() : [];
      const centrosData = resCentros.ok ? await resCentros.json() : [];
      const jornadasData = resJornadas.ok ? await resJornadas.json() : [];

      setUsuarios(usuariosData);
      setCentros(centrosData);

      const centrosById = new Map(centrosData.map((c) => [String(c.id), c]));
      const usuariosById = new Map(usuariosData.map((u) => [String(u.id), u]));

      const enriquecidas = jornadasData.map((j) => {
        const centroObj = centrosById.get(String(j.centro_id));
        const usuarioObj = usuariosById.get(String(j.usuario_id));
        const creador = usuariosById.get(String(j.creado_por_admin_id));

        const ini = parseDateSafe(j.inicio)?.getTime() ?? 0;
        const fin = parseDateSafe(j.fin)?.getTime() ?? 0;

        return {
          ...j,
          duracionMs: fin - ini,
          centro: centroObj ? centroObj.nombre : `Centro ${j.centro_id}`,
          usuario_nombre: usuarioObj
            ? `${usuarioObj.nombre} ${usuarioObj.apellidos || ""}`.trim()
            : "-",
          creado_por_nombre: creador
            ? `${creador.nombre} ${creador.apellidos || ""}`.trim()
            : "-",
        };
      });

      setJornadas(enriquecidas);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      toast.error("Error al cargar los datos iniciales.");
    }
  };

  // --------------------------
  // Scope usuarios visibles
  // --------------------------
  const usuariosEquipo = useMemo(() => {
    if (!esGerente) return [];
    return (usuarios || [])
      .filter((u) => String(u.gerente_id) === String(auth?.id))
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.trim().localeCompare(
          `${b.nombre} ${b.apellidos || ""}`.trim()
        )
      );
  }, [usuarios, auth?.id, esGerente]);

  const usuariosFiltrables = useMemo(() => {
    if (esAdmin) {
      return [...usuarios].sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.trim().localeCompare(
          `${b.nombre} ${b.apellidos || ""}`.trim()
        )
      );
    }

    if (esGerente) {
      return usuariosEquipo;
    }

    return usuarios.filter((u) => String(u.id) === String(auth?.id));
  }, [usuarios, usuariosEquipo, esAdmin, esGerente, auth?.id]);

  const usuarioSeleccionado =
    usuariosFiltrables.find((u) => String(u.id) === String(filtroUsuario)) || null;

  // --------------------------
  // Scope jornadas visibles
  // --------------------------
  const jornadasBaseVisibles = useMemo(() => {
    if (esAdmin) return jornadas;

    if (esGerente) {
      const idsEquipo = new Set(usuariosEquipo.map((u) => String(u.id)));
      return jornadas.filter((j) => idsEquipo.has(String(j.usuario_id)));
    }

    return jornadas.filter((j) => String(j.usuario_id) === String(auth?.id));
  }, [jornadas, esAdmin, esGerente, usuariosEquipo, auth?.id]);

  // --------------------------
  // Filtros
  // --------------------------
  const jornadasFiltradas = useMemo(() => {
    return jornadasBaseVisibles.filter((j) => {
      const dIni = parseDateSafe(j.inicio);
      const inicioISO = dIni ? dIni.toISOString().split("T")[0] : "";

      const matchUsuario = !filtroUsuario || String(j.usuario_id) === String(filtroUsuario);

      const matchCentro =
        !esAdmin || !filtroCentro ? true : String(j.centro_id) === String(filtroCentro);

      const matchFecha =
        !fechaDesde && !fechaHasta
          ? true
          : fechaDesde && !fechaHasta
          ? inicioISO === fechaDesde
          : fechaDesde && fechaHasta
          ? inicioISO >= fechaDesde && inicioISO <= fechaHasta
          : true;

      return matchUsuario && matchCentro && matchFecha;
    });
  }, [jornadasBaseVisibles, filtroUsuario, filtroCentro, fechaDesde, fechaHasta, esAdmin]);

  useEffect(() => {
    setPage(0);
  }, [filtroUsuario, filtroCentro, fechaDesde, fechaHasta, pageSize]);

  const limpiarFiltros = () => {
    setFiltroUsuario("");
    setFiltroCentro("");
    setFechaDesde("");
    setFechaHasta("");
    toast.info("Filtros limpiados.");
  };

  // --------------------------
  // Validación
  // --------------------------
  const toggleValidada = async (j) => {
    if (!puedeValidar) {
      toast.error("No tienes permisos para validar jornadas.");
      return;
    }

    try {
      const res = await fetch(`${Global.url}jornadas/${j.id}/validar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ validada: !j.validada }),
      });

      if (!res.ok) {
        toast.error(
          res.status === 403
            ? "No tienes permisos para validar esta jornada."
            : "Error al cambiar el estado de validación."
        );
        return;
      }

      const updated = await res.json();
      const ini = parseDateSafe(updated.inicio)?.getTime() ?? 0;
      const fin = parseDateSafe(updated.fin)?.getTime() ?? 0;

      setJornadas((prev) =>
        prev.map((row) =>
          row.id === j.id
            ? {
                ...row,
                ...updated,
                duracionMs: fin - ini,
              }
            : row
        )
      );

      toast.success(updated.validada ? "Jornada validada." : "Validación retirada.");
    } catch (e) {
      console.error(e);
      toast.error("Error de red al validar/desvalidar la jornada.");
    }
  };

  // --------------------------
  // Modal edición
  // --------------------------
  useEffect(() => {
    if (!modalEditar || !jornadaEditar) return;

    setEditInicio(toDatetimeLocalString(jornadaEditar.inicio));
    setEditFin(toDatetimeLocalString(jornadaEditar.fin));
    setEditMotivo(jornadaEditar.motivo_fin || "");
    setEditObs(
      jornadaEditar.observaciones_admin || jornadaEditar.comentario_admin || ""
    );
    setEditError("");
  }, [modalEditar, jornadaEditar]);

  const handleGuardarEdicion = async () => {
    if (!jornadaEditar) return;

    if (!puedeEditar) {
      toast.error("No tienes permisos para editar jornadas.");
      return;
    }

    if (!editInicio || !editFin) {
      setEditError("Debes indicar inicio y fin.");
      return;
    }

    if (!editObs.trim()) {
      setEditError("Debes introducir una observación.");
      return;
    }

    const dInicio = new Date(editInicio);
    const dFin = new Date(editFin);

    if (Number.isNaN(dInicio.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dInicio) {
      setEditError("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSavingEdit(true);

    try {
      const res = await fetch(`${Global.url}jornadas/modificar/${jornadaEditar.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          usuario_id: jornadaEditar.usuario_id,
          centro_id: jornadaEditar.centro_id,
          inicio: editInicio,
          fin: editFin,
          motivo_fin: editMotivo,
          observaciones_admin: editObs,
        }),
      });

      if (!res.ok) {
        toast.error("Error al modificar la jornada.");
        return;
      }

      toast.success("Jornada modificada correctamente.");
      setModalEditar(false);
      setJornadaEditar(null);

      await cargarDatos();
    } catch (e) {
      console.error(e);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSavingEdit(false);
    }
  };

  // --------------------------
  // Mapa
  // --------------------------
  const abrirMapa = async (j, focus = "inicio") => {
    setMapFocus(focus);

    const ini = j.latitud && j.longitud ? { lat: Number(j.latitud), lng: Number(j.longitud) } : null;
    const fin =
      j.latitud_cierre && j.longitud_cierre
        ? { lat: Number(j.latitud_cierre), lng: Number(j.longitud_cierre) }
        : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${j.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();

      if (res.ok && data?.latitud && data?.longitud) {
        setUbicacionCentro({
          lat: parseFloat(data.latitud),
          lng: parseFloat(data.longitud),
        });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }

    if (ini || fin) setMostrarMapa(true);
    else toast.warning("No hay coordenadas para mostrar.");
  };

  const mapCenter = useMemo(() => {
    const pick =
      mapFocus === "cierre"
        ? ubicacionCierre || ubicacionInicio
        : ubicacionInicio || ubicacionCierre;

    return pick ? [pick.lat, pick.lng] : [0, 0];
  }, [mapFocus, ubicacionInicio, ubicacionCierre]);

  // --------------------------
  // Exportación
  // --------------------------
  const exportarCSVReducido = (datos, nombreArchivo = "jornadas.csv") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const encabezadosAdmin = [
      "Usuario",
      "Centro",
      "Inicio",
      "Fin",
      "Duración",
      "En Rango (Inicio)",
      "Distancia (Inicio m)",
      "En Rango (Cierre)",
      "Distancia (Cierre m)",
      "Motivo Fin",
      "Creado por",
      "Observaciones",
      "Validada",
      "Validada por",
      "Validada en",
    ];

    const encabezadosNoAdmin = [
      "Usuario",
      "Centro",
      "Inicio",
      "Fin",
      "Duración",
      "Distancia (Inicio m)",
      "En Rango (Inicio)",
      "Distancia (Cierre m)",
      "En Rango (Cierre)",
      "Observaciones",
      "Motivo Fin",
    ];

    const encabezados = esAdmin ? encabezadosAdmin : encabezadosNoAdmin;

    const csvRows = [
      encabezados.join(","),
      ...datos.map((j) => {
        const filaAdmin = [
          j.usuario_nombre || nombreUsuario(j.usuario_id),
          j.centro ?? "",
          j.inicio ? fmtDate(j.inicio) : "-",
          j.fin ? fmtDate(j.fin) : "-",
          calcularDuracion(j.duracionMs),
          j.en_rango ? "En rango" : "Fuera de rango",
          j.distancia_m != null ? j.distancia_m : "",
          j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
          j.distancia_m_cierre != null ? j.distancia_m_cierre : "",
          j.motivo_fin ?? "-",
          j.creado_por_nombre || "-",
          j.observaciones_admin || "-",
          fmtBoolSN(!!j.validada),
          nombreValidador(j.validada_por),
          j.validada ? fmtDate(j.validada_en) : "-",
        ];

        const filaNoAdmin = [
          j.usuario_nombre || nombreUsuario(j.usuario_id),
          j.centro ?? "",
          j.inicio ? fmtDate(j.inicio) : "-",
          j.fin ? fmtDate(j.fin) : "-",
          calcularDuracion(j.duracionMs),
          j.distancia_m ?? "",
          j.en_rango ? "En rango" : "Fuera de rango",
          j.distancia_m_cierre ?? "",
          j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
          j.observaciones_admin ?? "-",
          j.motivo_fin ?? "-",
        ];

        const fila = esAdmin ? filaAdmin : filaNoAdmin;

        return fila.map((val) => `"${(val ?? "").toString().replace(/"/g, '""')}"`).join(",");
      }),
    ];

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nombreArchivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`CSV generado: ${nombreArchivo}`);
  };

  const exportarExcel = (datos, nombreArchivo = "jornadas.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const datosFormatoExcel = datos.map((j) =>
      esAdmin
        ? {
            Usuario: j.usuario_nombre || nombreUsuario(j.usuario_id),
            Centro: j.centro ?? "",
            Inicio: j.inicio ? fmtDate(j.inicio) : "-",
            Fin: j.fin ? fmtDate(j.fin) : "-",
            Duración: calcularDuracion(j.duracionMs),
            "En Rango (Inicio)": j.en_rango ? "En rango" : "Fuera de rango",
            "Distancia (Inicio m)": j.distancia_m != null ? j.distancia_m : "",
            "En Rango (Cierre)":
              j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
            "Distancia (Cierre m)": j.distancia_m_cierre != null ? j.distancia_m_cierre : "",
            "Motivo Fin": j.motivo_fin ?? "-",
            "Creado por": j.creado_por_nombre || "-",
            Observaciones: j.observaciones_admin || "-",
            Validada: fmtBoolSN(!!j.validada),
            "Validada por": nombreValidador(j.validada_por),
            "Validada en": j.validada ? fmtDate(j.validada_en) : "-",
          }
        : {
            Usuario: j.usuario_nombre || nombreUsuario(j.usuario_id),
            Centro: j.centro ?? "",
            Inicio: j.inicio ? fmtDate(j.inicio) : "-",
            Fin: j.fin ? fmtDate(j.fin) : "-",
            Duración: calcularDuracion(j.duracionMs),
            "Distancia (Inicio m)": j.distancia_m ?? "",
            "En Rango (Inicio)": j.en_rango ? "En rango" : "Fuera de rango",
            "Distancia (Cierre m)": j.distancia_m_cierre ?? "",
            "En Rango (Cierre)":
              j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
            Observaciones: j.observaciones_admin ?? "-",
            "Motivo Fin": j.motivo_fin ?? "-",
          }
    );

    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Jornadas");
    XLSX.writeFile(libro, nombreArchivo);

    toast.success(`Excel generado: ${nombreArchivo}`);
  };

  // --------------------------
  // Rows
  // --------------------------
  const rows = useMemo(
    () =>
      jornadasFiltradas.map((j) => ({
        ...j,
        usuario_nombre: j.usuario_nombre || nombreUsuario(j.usuario_id),
        duracion_txt: calcularDuracion(j.duracionMs),
        validador_txt: j.validada ? nombreValidador(j.validada_por) : "-",
        validada_en_txt: j.validada ? fmtDate(j.validada_en) : "-",
      })),
    [jornadasFiltradas, usuarios]
  );

  // --------------------------
  // Columns dinámicas por rol
  // --------------------------
  const columns = useMemo(() => {
    const comunes = [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },

      {
        field: "inicio",
        headerName: "Inicio",
        minWidth: 170,
        renderCell: (p) => fmtDate(p.row.inicio),
      },
      {
        field: "fin",
        headerName: "Fin",
        minWidth: 170,
        renderCell: (p) => fmtDate(p.row.fin),
      },

      { field: "duracion_txt", headerName: "Duración", minWidth: 120 },

      {
        field: "distancias",
        headerName: "Distancias",
        minWidth: 320,
        sortable: false,
        renderCell: (params) => {
          const j = params.row;
          const inicioOk = j.en_rango === true;
          const cierreOk = j.en_rango_cierre === true;
          const cierreUnknown = j.en_rango_cierre == null;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  pb: 0.25,
                  "&::-webkit-scrollbar": { height: 6 },
                }}
              >
                {j.distancia_m != null ? (
                  <Chip
                    icon={<MapIcon />}
                    label={`Inicio: ${j.distancia_m} m`}
                    size="small"
                    color={inicioOk ? "success" : "error"}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(j, "inicio")}
                  />
                ) : (
                  <Chip label="Inicio: -" size="small" variant="outlined" />
                )}

                {j.distancia_m_cierre != null ? (
                  <Chip
                    icon={<MapIcon />}
                    label={`Cierre: ${j.distancia_m_cierre} m`}
                    size="small"
                    color={cierreUnknown ? "default" : cierreOk ? "success" : "error"}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(j, "cierre")}
                  />
                ) : (
                  <Chip label="Cierre: -" size="small" variant="outlined" />
                )}
              </Stack>
            </Box>
          );
        },
      },
    ];

    const adminExtra = [
      {
        field: "validada",
        headerName: "Validada",
        minWidth: 120,
        renderCell: (params) =>
          params.row.validada ? (
            <Chip icon={<CheckCircleIcon />} label="Sí" size="small" color="success" />
          ) : (
            <Chip
              icon={<CancelIcon />}
              label="No"
              size="small"
              color="default"
              variant="outlined"
            />
          ),
      },
    ];

    const camposTextoAdmin = [
      { field: "validador_txt", headerName: "Validado por", minWidth: 220 },
      { field: "motivo_fin", headerName: "Motivo fin", minWidth: 180, flex: 1 },
      { field: "creado_por_nombre", headerName: "Creado por", minWidth: 180 },
      { field: "observaciones_admin", headerName: "Observaciones", minWidth: 260, flex: 1 },
      { field: "validada_en_txt", headerName: "Validada en", minWidth: 170 },
    ];

    const camposTextoNoAdmin = [
      { field: "observaciones_admin", headerName: "Observaciones", minWidth: 260, flex: 1 },
      { field: "motivo_fin", headerName: "Motivo fin", minWidth: 200, flex: 1 },
    ];

    const acciones = {
      field: "acciones",
      headerName: "Acciones",
      minWidth: esAdmin ? 250 : puedeEditar ? 160 : 120,
      sortable: false,
      renderCell: (params) => {
        const j = params.row;

        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            {puedeEditar && (
              <Button
                size="small"
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setJornadaEditar(j);
                  setModalEditar(true);
                }}
              >
                Editar
              </Button>
            )}

            {puedeValidar && (
              <Button size="small" variant="contained" onClick={() => toggleValidada(j)}>
                {j.validada ? "Quitar" : "Validar"}
              </Button>
            )}
          </Stack>
        );
      },
    };

    if (esAdmin) {
      return [...comunes, ...adminExtra, acciones, ...camposTextoAdmin];
    }

    return [...comunes, ...camposTextoNoAdmin, acciones];
  }, [esAdmin, puedeEditar, puedeValidar]);

  const titulo = esAdmin
    ? "Jornadas Finalizadas"
    : esGerente
    ? "Jornadas Finalizadas de mi equipo"
    : "Mis jornadas finalizadas";

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {titulo}
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: esAdmin ? "1fr 1fr" : "1fr 1fr",
                md: esAdmin ? "2fr 1fr 1fr 1fr auto" : "2fr 1fr 1fr auto",
              },
              alignItems: "end",
            }}
          >
            <Autocomplete
              size="small"
              options={usuariosFiltrables}
              getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={usuarioSeleccionado}
              onChange={(_, newValue) => setFiltroUsuario(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={esAdmin ? "Usuario" : esGerente ? "Usuario (mi equipo)" : "Usuario"}
                  placeholder="Escribe para buscar…"
                />
              )}
              clearOnEscape
            />

            {esAdmin && (
              <FormControl size="small">
                <InputLabel>Centro</InputLabel>
                <Select
                  label="Centro"
                  value={filtroCentro}
                  onChange={(e) => setFiltroCentro(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {centros
                    .slice()
                    .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
                    .map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.nombre}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />

            <TextField
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />

            <Button variant="contained" onClick={limpiarFiltros} sx={{ height: 40 }}>
              Limpiar
            </Button>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        {/* Exportaciones */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportarCSVReducido(jornadasFiltradas, "jornadas_filtradas.csv")}
            >
              CSV Filtradas
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportarCSVReducido(jornadasBaseVisibles, "todas_jornadas.csv")}
            >
              CSV Todas
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportarExcel(jornadasFiltradas, "jornadas_filtradas.xlsx")}
            >
              Excel Filtradas
            </Button>

            <Button
              startIcon={<DownloadIcon />}
              onClick={() => exportarExcel(jornadasBaseVisibles, "todas_jornadas.xlsx")}
            >
              Excel Todas
            </Button>

            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={cargarDatos}>
              Refrescar
            </Button>
          </Stack>
        </Paper>

        {/* MODAL EDICIÓN */}
        <Dialog
          open={modalEditar}
          onClose={() => {
            if (savingEdit) return;
            setModalEditar(false);
            setJornadaEditar(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Modificar jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Inicio"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editInicio}
                onChange={(e) => setEditInicio(e.target.value)}
              />

              <TextField
                label="Fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editFin}
                onChange={(e) => setEditFin(e.target.value)}
              />

              <TextField
                label="Motivo fin"
                value={editMotivo}
                onChange={(e) => setEditMotivo(e.target.value)}
                placeholder="Motivo de la modificación"
              />

              <TextField
                label="Observaciones"
                value={editObs}
                onChange={(e) => setEditObs(e.target.value)}
                placeholder="Observaciones administrativas"
                multiline
                minRows={3}
              />

              {editError && <Typography color="error">{editError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setModalEditar(false);
                setJornadaEditar(null);
              }}
              disabled={savingEdit}
            >
              Cancelar
            </Button>
            <Button onClick={handleGuardarEdicion} disabled={savingEdit}>
              {savingEdit ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* MODAL MAPA */}
        <Dialog open={mostrarMapa} onClose={() => setMostrarMapa(false)} fullWidth maxWidth="md">
          <DialogTitle>Ubicación de la jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2 }}>
            {ubicacionInicio || ubicacionCierre ? (
              <Box sx={{ width: "100%", height: 460 }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satélite + Callejero">
                      <TileLayer
                        url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}&language=es`}
                        attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  {ubicacionInicio && (
                    <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        📍 Jornada (inicio)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCierre && (
                    <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        ✅ Jornada (cierre)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCentro && (
                    <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        🏢 Centro de trabajo
                      </Tooltip>
                    </Marker>
                  )}
                </MapContainer>
              </Box>
            ) : (
              <Typography>No hay coordenadas para mostrar.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setMostrarMapa(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};